/* The existing product form remains the only source of order data. */
(() => {
  if (customElements.get('appa-bowl-preview')) return;
  let viewerLibrary;
  const retainedNames = new Map();
  const read = async url => {
    const response = await fetch(url);
    if (!response.ok) throw Error('Asset unavailable');
    return response.text();
  };
  class BowlPreview extends HTMLElement {
    static assetURLs(urls, base) {
      const output = {...urls,models:{}};
      for (const key of ['worker','viewer','data','environment']) output[key] = new URL(urls[key],base).href;
      for (const [key,value] of Object.entries(urls.models)) output.models[key] = new URL(value,base).href;
      // model-viewer recognises HDR by the URL suffix, so Shopify's ?v= must be removed.
      const environment = new URL(output.environment); environment.search = ''; environment.hash = '';
      output.environment = environment.href;
      return output;
    }
    static selection(config, design, variant, rawName) {
      const name = rawName.trim().toUpperCase();
      // Match the existing form's A–Z / ten-character rule; never rewrite its value.
      if (name && !/^[A-Z]{1,10}$/.test(name)) return {error:'invalid'};
      if (!variant) return {error:'unmapped'};
      const colour = variant.colour || config.colours[variant.options[0]?.trim().toLowerCase()];
      const rings = design === 'donut' ? variant.options[1]?.match(/^([234])(?:\s|$)/)?.[1] : null;
      if (!colour || (design !== 'ribbed' && !rings)) return {error:'unmapped'};
      return {name, colour, variant:design === 'ribbed' ? 'ribbed' : `donut_${rings}_layers`, title:variant.title};
    }
    connectedCallback() {
      if (this.controller) return;
      this.nameInput = this.form()?.querySelector('#custom-text-1');
      if (this.nameInput && retainedNames.has(this.dataset.formId) && !this.nameInput.value) {
        this.nameInput.value = retainedNames.get(this.dataset.formId);
        this.nameInput.dispatchEvent(new Event('input',{bubbles:true}));
      }
      this.config = JSON.parse(this.querySelector('[data-preview-config]').textContent);
      this.config.urls = BowlPreview.assetURLs(this.config.urls,document.baseURI);
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('[data-preview-stage]');
      this.status = this.querySelector('[data-preview-status]');
      this.arButton = this.querySelector('[data-preview-ar]');
      this.previewNameInput = this.querySelector('[data-preview-name]');
      this.controller = new AbortController();
      this.sequence = 0; this.urls = new Set();
      this.mountDock();
      const options = {signal:this.controller.signal};
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-appa-preview-open]');
        if (trigger && (this.contains(trigger) || this.dock?.contains(trigger) || trigger.closest('#sticky-add-to-cart'))) this.open(trigger);
      }, options);
      this.querySelector('[data-preview-close]').addEventListener('click', () => this.close(), options);
      this.querySelector('[data-preview-done]').addEventListener('click', () => this.close(), options);
      // Keep the original order-property input and its existing validation/counter listeners.
      const editName = event => {
        if (event.isComposing || !this.nameInput) return;
        this.nameInput.value = this.previewNameInput.value;
        this.nameInput.dispatchEvent(new Event('input',{bubbles:true}));
      };
      this.previewNameInput.addEventListener('input', editName, options);
      this.previewNameInput.addEventListener('compositionend', editName, options);
      this.previewNameInput.addEventListener('keydown', event => {
        if (event.key === 'Enter' && !event.isComposing) event.preventDefault();
      }, options);
      this.arButton.addEventListener('click', async () => {
        if (!this.viewer?.canActivateAR) { this.setStatus('arUnsupported'); return; }
        try {
          // Call immediately within the user gesture so iOS can open Quick Look.
          await this.viewer.activateAR();
        } catch (error) { this.setStatus('arError'); }
      }, options);
      this.querySelector('[data-preview-retry]').addEventListener('click', () => {
        this.resetWorker(); this.refresh();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.unlockPage();
        this.sequence++; clearTimeout(this.timer); clearTimeout(this.timeout);
        this.stage.setAttribute('aria-busy','false');
        this.arButton.disabled = true;
        this.viewer?.removeAttribute('src'); this.releaseURLs();
        this.trigger?.focus({preventScroll:true});
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
      }, options);
      document.addEventListener('input', event => {
        if (event.target === this.nameInput) {
          retainedNames.set(this.dataset.formId,event.target.value);
          this.syncNameInput();
          if (this.dialog.open) this.schedule();
        }
      }, options);
      window.addEventListener('themeVariantUpdated', () => { this.showButtons(); if (this.dialog.open) this.schedule(); }, options);
      window.addEventListener('pageshow', () => this.showButtons(), options);
      window.addEventListener('resize', () => this.syncDockHeight(), options);
      this.showButtons();
    }
    disconnectedCallback() {
      // The theme replaces the whole buy-button form after a variant request.
      if (this.nameInput) retainedNames.set(this.dataset.formId,this.nameInput.value);
      this.dockObserver?.disconnect();
      if (this.dock?.classList.contains('appa-preview-dock')) {
        this.dock.classList.remove('appa-preview-dock');
        this.dock.hidden = true;
        this.append(this.dock);
        this.dockSpacer?.remove();
        document.documentElement.classList.remove('appa-bowl-docked');
        document.documentElement.style.removeProperty('--appa-preview-dock-height');
      }
      this.controller?.abort(); this.controller = null;
      clearTimeout(this.timer); clearTimeout(this.timeout);
      this.dialog?.close(); this.unlockPage(); this.resetWorker(); this.releaseURLs();
      document.querySelectorAll('#sticky-add-to-cart [data-appa-preview-open]').forEach(b => { b.hidden = true; });
    }
    form() { return document.getElementById(this.dataset.formId); }
    syncNameInput() {
      const value = this.nameInput?.value || '';
      if (this.previewNameInput.value === value) return;
      const {selectionStart,selectionEnd} = this.previewNameInput;
      this.previewNameInput.value = value;
      if (document.activeElement === this.previewNameInput) this.previewNameInput.setSelectionRange(selectionStart,selectionEnd);
    }
    mountDock() {
      this.dock = this.querySelector('[data-preview-dock]');
      if (!this.dock) return;
      if (!this.dataset.formId.startsWith('main-product-form-') || this.dataset.formId.includes('quickshop')) {
        this.dock.hidden = false;
        return;
      }
      this.dock.classList.add('appa-preview-dock');
      this.dockSpacer = document.createElement('div');
      this.dockSpacer.className = 'appa-preview-dock-spacer';
      this.dockSpacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.dockSpacer, this.dock);
      if (window.ResizeObserver) {
        this.dockObserver = new ResizeObserver(() => this.syncDockHeight());
        this.dockObserver.observe(this.dock);
      }
    }
    syncDockHeight() {
      if (!this.isConnected || !this.dock?.classList.contains('appa-preview-dock')) return;
      const height = this.dock.hidden ? 0 : Math.ceil(this.dock.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--appa-preview-dock-height', height + 'px');
    }
    showButtons() {
      const buttons = [...this.querySelectorAll('[data-appa-preview-open]'), ...document.querySelectorAll('#sticky-add-to-cart [data-appa-preview-open]')];
      if (this.dock) buttons.push(...this.dock.querySelectorAll('[data-appa-preview-open]'));
      for (const button of buttons) button.hidden = !this.form();
      if (this.dock?.classList.contains('appa-preview-dock')) {
        this.dock.hidden = !this.form();
        document.documentElement.classList.toggle('appa-bowl-docked', !this.dock.hidden);
        this.syncDockHeight();
      }
    }
    currentSelection() {
      const form = this.form();
      const section = form?.closest('.m6pr');
      const values = [...(section?.querySelectorAll('.f8pr-variant-selection input:checked, .f8pr-variant-selection select option:checked') || [])].map(el => el.value);
      // Read current picker choices first; the theme replaces its hidden ID asynchronously.
      const variant = values.length ? this.config.variants.find(v => v.options.every(value => values.includes(value))) : this.config.variants.find(v => v.id === form?.querySelector('[name="id"]')?.value);
      return BowlPreview.selection(this.config,this.dataset.design,variant,form?.querySelector('#custom-text-1')?.value || '');
    }
    open(trigger) {
      if (this.dialog.open) return;
      this.syncNameInput();
      const position = { left: window.scrollX, top: window.scrollY };
      this.trigger = trigger; this.dialog.showModal();
      this.lockPage(position);
      this.querySelector('[data-preview-close]').focus({ preventScroll: true });
      this.refresh();
    }
    close() { this.dialog.close(); }
    lockPage(position) {
      if (this.pageScrollLock) return;
      const root = document.documentElement;
      const body = document.body;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right', 'box-sizing'];
      const styles = properties.map(property => [property, body.style.getPropertyValue(property), body.style.getPropertyPriority(property)]);
      const gap = Math.max(0, window.innerWidth - root.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      this.pageScrollLock = { position, styles, wasLocked: root.classList.contains('appa-preview-scroll-locked') };
      root.classList.add('appa-preview-scroll-locked');
      Object.assign(body.style, { position: 'fixed', top: '-' + position.top + 'px', left: '0', right: '0', width: '100%', overflow: 'hidden', boxSizing: 'border-box' });
      if (gap > 0) body.style.paddingRight = (padding + gap) + 'px';
    }
    unlockPage() {
      if (!this.pageScrollLock) return;
      const { position, styles, wasLocked } = this.pageScrollLock;
      this.pageScrollLock = null;
      for (const [property, value, priority] of styles) {
        if (value) document.body.style.setProperty(property, value, priority);
        else document.body.style.removeProperty(property);
      }
      if (!wasLocked) document.documentElement.classList.remove('appa-preview-scroll-locked');
      window.scrollTo({ ...position, behavior: 'instant' });
    }
    schedule() {
      clearTimeout(this.timer); this.sequence++; this.arButton.disabled = true;
      this.timer = setTimeout(() => this.refresh(),160);
    }
    setStatus(key, error = false) {
      this.status.textContent = key === 'ready' ? '' : (this.config.text[key] || this.config.text.error);
      this.status.hidden = !this.status.textContent;
      this.status.toggleAttribute('data-error',error);
      this.querySelector('[data-preview-retry]').hidden = !error;
    }
    async ensureRuntime() {
      if (!window.Worker || !window.OffscreenCanvas || !window.DecompressionStream || !HTMLDialogElement.prototype.showModal) throw Error('unsupported');
      if (!customElements.get('model-viewer')) {
        viewerLibrary ||= import(this.config.urls.viewer).catch(error => { viewerLibrary = null; throw error; });
        await viewerLibrary;
      }
      if (!this.viewer) {
        this.viewer = document.createElement('model-viewer');
        const attributes = {'ar':'','ar-modes':'webxr quick-look','ar-scale':'fixed','ar-placement':'floor','camera-controls':'','touch-action':'pan-y','interaction-prompt':'auto','environment-image':this.config.urls.environment,'tone-mapping':'neutral','exposure':'.72','shadow-intensity':'.72','shadow-softness':'.9','camera-orbit':'0deg 67deg 110%','field-of-view':'30deg','min-camera-orbit':'auto 10deg 55%','max-camera-orbit':'auto 100deg 180%',alt:this.config.text.alt};
        for (const [key,value] of Object.entries(attributes)) this.viewer.setAttribute(key,value);
        // Use our mobile overlay AR button instead of the viewer's duplicate.
        const arSlot = document.createElement('span'); arSlot.slot = 'ar-button'; arSlot.hidden = true;
        this.viewer.append(arSlot);
        this.viewer.addEventListener('ar-status', event => {
          if (event.detail.status === 'failed') this.setStatus('arError');
        });
        this.viewer.addEventListener('load', () => {
          if (!this.dialog.open || this.displayed !== this.sequence || this.viewer.getAttribute('src') !== this.currentURL) return;
          clearTimeout(this.timeout); this.stage.setAttribute('aria-busy','false'); this.setStatus('ready'); this.arButton.disabled = false;
          this.releaseURLs(this.currentURL);
        });
        this.viewer.addEventListener('error', event => { console.warn('[APPA 3D] Model could not load'); this.fail('error'); });
        this.stage.prepend(this.viewer);
      }
      if (!this.worker) {
        // CDN assets have a different origin. A local Blob worker avoids cross-origin Worker errors.
        const source = await read(this.config.urls.worker);
        if (this.worker || !this.isConnected || !this.dialog.open) return;
        const url = URL.createObjectURL(new Blob([source],{type:'text/javascript'}));
        try { this.worker = new Worker(url); } finally { URL.revokeObjectURL(url); }
        this.worker.addEventListener('message', ({data}) => this.receive(data));
        this.worker.addEventListener('error', event => { console.warn('[APPA 3D] Worker could not start'); this.fail('error'); this.resetWorker(); });
        this.worker.postMessage({init:true,urls:this.config.urls});
      }
    }
    async refresh() {
      if (!this.dialog.open) return;
      const id = ++this.sequence, selection = this.currentSelection();
      this.arButton.disabled = true;
      this.stage.setAttribute('aria-busy','true'); this.setStatus('loading');
      clearTimeout(this.timeout);
      if (selection.error) { this.fail(selection.error); return; }
      this.querySelector('[data-preview-selection]').textContent = selection.title;
      this.timeout = setTimeout(() => { if (id === this.sequence) { this.fail('error'); this.resetWorker(); } },45000);
      try {
        await this.ensureRuntime();
        if (id !== this.sequence || !this.dialog.open || !this.isConnected) return;
        this.worker.postMessage({id,...selection});
      } catch (error) { console.warn('[APPA 3D] '+error.message); if (id === this.sequence) this.fail(error.message === 'unsupported' ? 'unsupported' : 'error'); }
    }
    receive(data) {
      if (data.id !== this.sequence || !this.dialog.open) return;
      if (data.error) { console.warn('[APPA 3D] '+data.error); this.fail('error'); return; }
      this.currentURL = URL.createObjectURL(new Blob([data.buffer],{type:'model/gltf-binary'}));
      this.urls.add(this.currentURL); this.displayed = data.id;
      this.viewer.setAttribute('src',this.currentURL);
    }
    fail(key) {
      clearTimeout(this.timeout); this.setStatus(key,true); this.arButton.disabled = true;
      this.stage.setAttribute('aria-busy','true');
      this.viewer?.removeAttribute('src'); this.releaseURLs();
    }
    releaseURLs(keep) { for (const url of this.urls || []) if (url !== keep) { URL.revokeObjectURL(url); this.urls.delete(url); } }
    resetWorker() { this.worker?.terminate(); this.worker = null; }
  }
  customElements.define('appa-bowl-preview',BowlPreview);
})();
